import { ContactMail, Delete, PlusOne, SearchOff } from "@mui/icons-material";
import CachedIcon from "@mui/icons-material/Cached";
import { Box, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Toolbar, Tooltip, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addActivityManager, disableActivityManager } from "../../api/activity";
import { addProcedureManager, disableProcedureManager, getProcedure, setContactManager } from "../../api/procedure";
import { disableProjectManager } from "../../api/projects";
import { listManagers } from "../../api/user/listManagers";
import { useTranslationHook } from "../../hooks";
import { setLoading } from "../../store/UI";
import { ManagerLevel } from "../../types/model/account";
import { Manager } from "../../types/model/user";
import { StoreInterfaces } from "../../types/store";
import ManagerSelector from "./ManagerSelector";
import { addProjectManager } from "../../api/project";
import { addAccountManager } from "../../api/account/addAccountManager";
import { disableAccountManager } from "../../api/account";

interface ManagerListProps {
  accountId: number;
  projectId?: number;
  activityId?: number;
  procedureId?: number;
  managerLevel: ManagerLevel | null;
  isObjectClosedOrCanceled: boolean;
  readOnly: boolean;
}

const ManagersList = ({
  accountId,
  projectId,
  activityId,
  procedureId,
  managerLevel,
  isObjectClosedOrCanceled,
  readOnly
}: ManagerListProps) => {
  const { t } = useTranslationHook();
  const dispatch = useDispatch();

  const [managerList, setManagerList] = useState<Manager[]>([]);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [contactManagerId, setContactManagerId] = useState<number | null>(null);

  const session = useSelector((state: StoreInterfaces) => state.session);
  
  let pageContext: "account" | "procedure" | "activity" | "project";
  if (procedureId) {
    pageContext = "procedure";
  } else if (activityId) {
    pageContext = "activity";
  } else if (projectId) {
    pageContext = "project";
  } else {
    pageContext = "account";
  }

  const LEVEL_RANK: Record<string, number> = {
    account: 0,
    project: 1,
    activity: 2,
    procedure: 3,
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const fetchManagers = async () => {
    dispatch(setLoading(true));
    try {
      const data = await listManagers(accountId, projectId, activityId, procedureId);
      setManagerList(data);
      enqueueSnackbar(t("manager.managers-loaded"), { variant: "success" });

      if (projectId && activityId && procedureId) {
        const proc = await getProcedure(projectId, activityId, procedureId);
        setContactManagerId(proc.contactManagerId || null);
      }
    } catch (error) {
      enqueueSnackbar(t("error.managers-loaded-error"), { variant: "error" });
      console.error(error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    fetchManagers();
  }, []);

  const handleDelete = async (id: number) => {
    try {
      if( projectId && activityId && procedureId ) {
        await disableProcedureManager(projectId, activityId, procedureId, id);
      } else if (projectId && activityId) {
        await disableActivityManager(projectId, activityId, id);
      } else if (projectId) {
        await disableProjectManager(projectId, id);
      } else {
        await disableAccountManager(accountId, id);
      }
      enqueueSnackbar(t("manager.delete-success"), { variant: "success" });
      fetchManagers();
    } catch (error) {
      enqueueSnackbar(t("error.managers-deleted-error"), { variant: "error" });
      console.error(error);
    }
  };

  const handleSetContact = async (id: number) => {
    if (!projectId || !activityId || !procedureId) return;

    try {
      await setContactManager(projectId, activityId, procedureId, id);
      setContactManagerId(id);
      enqueueSnackbar(t("manager.set-contact-manager-success"), {
        variant: "success",
      });
    } catch (error){
      enqueueSnackbar(t("error.set-contact-manager-error"), {
        variant: "error",
      });
      console.error(error);
    }
  };

  const visibleRows = managerList.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getDeleteReason = (manager: Manager): string => {
    if (!session.isManager)
      return "";
    if (isObjectClosedOrCanceled)
      return t("manager.delete-disabled-closed-or-canceled");
    if (manager.level !== pageContext)
      return t("manager.delete-disabled-level");
    if (!managerLevel || managerLevel.level === null)
      return t("manager.delete-disabled-no-context");
    if (LEVEL_RANK[managerLevel.level] > LEVEL_RANK[pageContext])
      return t("manager.delete-disabled-insufficient-rank");
    return "";
  };

  const title = (() => {
    if (procedureId) return t("procedures.managers");
    if (activityId) return t("activities.managers");
    if (projectId) return t("projects.managers");
    return t("accounts.tabs.managers");
  })();

  const showActionsColumn = session.isManager || (!managerLevel && !!contactManagerId) || pageContext === "procedure";

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
      <Paper sx={{ p: 2, flex: 1 }}>
        <Toolbar sx={{ gap: 1 }}>
          <Typography sx={{ flex: 1 }} variant="h5">
            {title}
          </Typography>

          <Tooltip title={t("generic.refresh")}>
            <IconButton onClick={fetchManagers}>
              <CachedIcon />
            </IconButton>
          </Tooltip>

          {(session.isManager || session.isAdministrator) && !readOnly && (
            <Tooltip title={t("manager.add")}>
              <IconButton
                onClick={() => setSelectorOpen(true)}
                disabled={isObjectClosedOrCanceled}
              >
                <PlusOne />
              </IconButton>
            </Tooltip>
          )}
        </Toolbar>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("generic.name")}</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>{t("generic.role.label")}</TableCell>
                {showActionsColumn && (
                  <TableCell align="right">
                    {t("generic.actions")}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                    <Stack alignItems="center" spacing={2} sx={{ opacity: 0.6 }}>
                      <SearchOff sx={{ fontSize: 60 }} />
                      <Typography>
                        {t("manager.no-managers")}
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((m) => {
                  const deleteDisabled = (() => {
                    // Not an app manager
                    if (!session.isManager) return true;

                    // Object is closed or canceled
                    if (isObjectClosedOrCanceled) return true;

                    // Manager must match current page context level
                    if (m.level !== pageContext) return true;

                    // User rank must be <= page context rank
                    if (!managerLevel || managerLevel.level === null) return true;
                    return LEVEL_RANK[managerLevel.level] > LEVEL_RANK[pageContext];
                  })();

                  return (
                    <TableRow key={m.id} hover>
                      <TableCell>{m.name}</TableCell>
                      <TableCell>{m.email}</TableCell>
                      <TableCell>
                        {t("manager.manager-level-" + m.level)}
                      </TableCell>

                      {showActionsColumn && (
                        <TableCell align="right">
                          {!!activityId && !!procedureId && (
                            managerLevel ? (
                              <Tooltip title={t("manager.set-contact-manager")}>
                                <IconButton
                                  onClick={() => handleSetContact(m.id)}
                                  color={m.id === contactManagerId ? "primary" : "default"}
                                >
                                  <ContactMail />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              m.id === contactManagerId && (
                                <Tooltip title={t("manager.this-is-your-contact")}>
                                  <ContactMail color="primary" sx={{ fontSize: 20, verticalAlign: "middle" }} />
                                </Tooltip>
                              )
                            )
                          )}

                          {/* Delete Button */}
                          {session.isManager && (
                            <Tooltip title={deleteDisabled ? getDeleteReason(m) : t("generic.delete")}>
                              <span>
                                <IconButton
                                  onClick={() => handleDelete(m.id)}
                                  disabled={deleteDisabled}
                                  color={deleteDisabled ? "default" : "error"}
                                >
                                  <Delete />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={managerList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setPage(0);
          }}
        />
      </Paper>

      <ManagerSelector
        open={selectorOpen}
        context={pageContext}
        accountId={accountId}
        projectId={projectId}
        activityId={activityId}
        procedureId={procedureId}
        onSelect={async (id) => {
          if (projectId && activityId && procedureId) {
            await addProcedureManager(projectId, activityId, procedureId, id);
          } else if (projectId && activityId) {
            await addActivityManager(projectId, activityId, id);
          } else if (projectId) {
            await addProjectManager(projectId, id);
          } else {
            await addAccountManager(accountId, id);
          }
          fetchManagers();
          setSelectorOpen(false);
        }}
        onCancel={() => setSelectorOpen(false)}
      />
    </Box>
  );
};

export default ManagersList;